
import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MessageCircleQuestion, Users, Code, Clock, Wrench, DollarSign, TrendingUp, Heart } from 'lucide-react';
import SectionLayout from '@/components/SectionLayout';

const faqItems = [
  {
    question: "Who is this \"Local AI Assistant Launchpad\" course really for?",
    answer: "This course is perfect for local marketing agency owners, web designers, SEO specialists, and lead generation experts who want to offer a simple, high-demand AI service to small local businesses. It's especially great if you're looking for an easy \"foot-in-the-door\" service, want to add recurring revenue, or feel a bit stuck selling the same commoditized services as everyone else. If you're good at the \"doing\" but want to get better and more confident at the \"selling,\" this is for you!",
    icon: Users
  },
  {
    question: "Do I need to be super techy or know how to code to do this?",
    answer: "Absolutely not! That's the beauty of what we teach and the tools we recommend (like AskSpot). The entire process of building these simple AI Assistants is designed to be no-code and incredibly user-friendly. If you can use a computer and follow simple step-by-step instructions, you can do this.",
    icon: Code
  },
  {
    question: "How long will it take me to go through the course?",
    answer: "The course is designed to be actionable and concise. You can likely go through all the core video modules in just a few hours. The real magic happens when you start implementing, and we aim to get you ready to build your first AI Assistant and approach your first client very quickly!",
    icon: Clock
  },
  {
    question: "What kind of AI Assistants will I learn to build? Are they complicated?",
    answer: "We focus on teaching you how to build simple but highly effective AI Assistants designed to answer Frequently Asked Questions (FAQs) for local businesses – things like their hours, location, services, how to book, etc. These aren't complicated, voice-activated, multi-integration behemoths. They are easy-to-build, easy-to-manage \"FAQ Fixers\" that solve a huge pain point for SBOs.",
    icon: Wrench
  },
  {
    question: "Do I need to have an AskSpot account or use AskSpot to benefit from the strategies taught in this course?",
    answer: "That's a great question! While we (Dave Calafiore and the AskSpot team) are the creators of AskSpot and naturally believe it's the simplest and most efficient platform for building the types of AI Assistants we teach in this course, the core strategies for prospecting, selling, client onboarding, and identifying client needs are not exclusive to AskSpot. The principles of finding businesses with FAQ overload, demonstrating value, and using this as a \"foot-in-the-door\" can be applied even if you choose to use other platforms like GoHighLevel (GHL) or similar tools to build simple chatbots.\n\nHowever, the step-by-step \"how-to-build\" demonstrations (Module 2) and specific tool-related assets will be focused on using AskSpot because we want to provide the clearest, easiest path for our students to get started quickly. You'll find AskSpot exceptionally well-suited and streamlined for this specific \"Local AI Assistant\" model.",
    icon: MessageCircleQuestion
  },
  {
    question: "How much can I charge for these AI Assistant services?",
    answer: "In the course, we provide guidance and examples for our \"Easy Launch\" package, which typically includes a very affordable one-time setup fee (e.g., in the $99-$249 range) and a low monthly recurring fee (e.g., $29-$59/month) for ongoing service and maintenance. The exact pricing is up to you and what your local market will support, but we focus on making it a \"no-brainer\" offer for small businesses.",
    icon: DollarSign
  },
  {
    question: "Is this just about selling one small service? How does it help my agency grow?",
    answer: "While the AI Assistant itself is simple, its power as a \"foot-in-the-door\" service is immense! It allows you to solve an immediate pain point for a client, build trust quickly, and demonstrate your value. The insights you gain from their AI Assistant's interactions can then naturally lead to conversations about your other, higher-value services like web design, SEO, ad management, etc. It's a fantastic client acquisition and relationship-building tool.",
    icon: TrendingUp
  },
  {
    question: "What if I'm terrible at sales or hate prospecting?",
    answer: "This course was practically made for you! We specifically designed the prospecting and sales modules (Modules 3 & 4) to be low-pressure, value-driven, and non-salesy. We give you simple scripts and a demo process that focuses on solving the client's problem, making the \"sale\" feel more like a helpful conversation.",
    icon: Heart
  }
];

const LaunchpadFAQ = () => {
  return (
    <SectionLayout
      paddingY="py-12 sm:py-16 md:py-20"
      backgroundColor="bg-gradient-to-b from-white via-[#FEE6D8]/10 to-white"
      containerWidth="medium"
      decorativeElements={
        <>
          {/* Floating question marks */}
          <div className="absolute top-[15%] left-[8%] animate-float opacity-20 hidden sm:block">
            <div className="w-8 h-8 bg-[#FEE6D8]/60 rounded-full flex items-center justify-center">
              <span className="text-[#003a70] font-bold text-sm">?</span>
            </div>
          </div>
          
          <div className="absolute top-[60%] right-[12%] animate-float opacity-25 hidden sm:block" style={{animationDelay: '2s'}}>
            <div className="w-6 h-6 bg-[#003a70]/30 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-xs">?</span>
            </div>
          </div>
          
          {/* Gradient orbs */}
          <div className="absolute top-[20%] right-[20%] w-40 h-40 bg-gradient-to-br from-[#FEE6D8]/20 to-[#003a70]/10 rounded-full blur-2xl animate-pulse opacity-50 hidden sm:block"></div>
          <div className="absolute bottom-[15%] left-[15%] w-32 h-32 bg-gradient-to-br from-[#003a70]/15 to-[#FEE6D8]/25 rounded-full blur-xl animate-float opacity-40 hidden sm:block" style={{animationDelay: '1.5s'}}></div>
        </>
      }
    >
      <div className="max-w-4xl mx-auto px-3 sm:px-4">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <div className="inline-flex items-center justify-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center shadow-lg">
              <MessageCircleQuestion className="text-white h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#003a70] leading-tight">
              Frequently Asked Questions
            </h2>
          </div>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed px-2">
            Everything you need to know about the Local AI Assistant Launchpad course
          </p>
        </div>
        
        {/* FAQ Accordion */}
        <div className="space-y-3 sm:space-y-4">
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border border-[#FEE6D8]/40 rounded-lg sm:rounded-xl mb-3 sm:mb-4 bg-white/80 backdrop-blur-sm shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden group"
                >
                  <AccordionTrigger className="px-4 py-4 sm:px-5 sm:py-5 md:px-6 md:py-5 text-left font-semibold text-sm sm:text-base md:text-lg text-[#003a70] hover:no-underline group-hover:bg-[#FEE6D8]/10 transition-colors">
                    <div className="flex items-start gap-3 w-full">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-gradient-to-br from-[#FEE6D8] to-[#FEE6D8]/70 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                        <IconComponent className="text-[#003a70] h-4 w-4 sm:h-4.5 sm:w-4.5 md:h-5 md:w-5" />
                      </div>
                      <span className="text-left leading-snug sm:leading-relaxed pr-2">{item.question}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 sm:px-5 sm:pb-5 md:px-6 md:pb-5">
                    <div className="ml-10 sm:ml-11 md:ml-14">
                      <div className="text-gray-700 leading-relaxed text-sm sm:text-base whitespace-pre-line">
                        {item.answer}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
        
        {/* Bottom CTA */}
        <div className="text-center mt-10 sm:mt-12 md:mt-16">
          <div className="bg-gradient-to-r from-[#003a70]/5 via-[#FEE6D8]/20 to-[#003a70]/5 rounded-xl sm:rounded-2xl px-4 py-6 sm:px-6 sm:py-8 md:px-8 md:py-10 border border-[#FEE6D8]/30">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-[#003a70] mb-3 sm:mb-4">
              Still Have Questions?
            </h3>
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base leading-relaxed px-2">
              We're here to help! Get in touch and we'll answer any questions you have about the course.
            </p>
            <div className="inline-flex items-center gap-2 text-[#003a70] font-semibold hover:text-[#003a70]/80 transition-colors cursor-pointer text-sm sm:text-base">
              <MessageCircleQuestion className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Contact Support</span>
            </div>
          </div>
        </div>
      </div>
    </SectionLayout>
  );
};

export default LaunchpadFAQ;
